import { Api, IErrorModel } from "@model";
import {
  Button,
  Card,
  Dropdown,
  Popconfirm,
  Select,
  Space,
  Input,
  message,
  Modal,
  MenuProps,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useIsCompact } from "../../../hooks/useIsCompact";
import {
  DeleteOutlined,
  SaveOutlined,
  SearchOutlined,
  MoreOutlined,
  CopyOutlined,
} from "@ant-design/icons";

const ErrorLogs = () => {
  const isCompact = useIsCompact();
  const [errors, setErrors] = useState<IErrorModel[]>();
  const [errorCount] = useState<number>(0);
  const [errorFetchLimit, setErrorFetchLimit] = useState(100);
  const [searchId, setSearchId] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);

  const getData = useCallback(() => {
    Api.get(`/api/admin/error-logger?limit=${errorFetchLimit}`).then((a) => {
      setErrors(a.data.data);
    });
  }, [errorFetchLimit]);

  useEffect(() => {
    if (!searchId) {
      getData();
    }
  }, [errorFetchLimit, searchId, getData]);

  function searchById() {
    if (!searchId.trim()) {
      message.warning("Please enter an error ID");
      return;
    }
    setIsSearching(true);
    Api.get(`/api/admin/error-logger/${searchId.trim()}`)
      .then((response) => {
        if (response.data.status === "not_found") {
          message.error("Error not found");
          setErrors([]);
        } else {
          setErrors([response.data.data]);
        }
      })
      .catch((error) => {
        message.error("Failed to fetch error");
        console.error(error);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }

  console.log({ errors });

  return (
    <div>
      <Space
        direction={isCompact ? "vertical" : "horizontal"}
        style={{ marginBottom: 16, width: "100%" }}
        wrap
      >
        <b>Total Errors Count : ({errorCount})</b>
        <Select
          defaultValue="100"
          style={{ width: isCompact ? "100%" : 120 }}
          onChange={(e) => {
            setErrorFetchLimit(+e);
            setSearchId("");
          }}
          options={[
            { value: "25", label: "25" },
            { value: "50", label: "50" },
            { value: "100", label: "100" },
            { value: "500", label: "500" },
          ]}
        />
        <Input
          placeholder="Search by Error ID"
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          onPressEnter={searchById}
          style={{ width: isCompact ? "100%" : 300 }}
          suffix={
            <Button
              type="text"
              icon={<SearchOutlined />}
              onClick={searchById}
              loading={isSearching}
            />
          }
        />
        {searchId && (
          <Button
            onClick={() => {
              setSearchId("");
              getData();
            }}
          >
            Clear Search
          </Button>
        )}
      </Space>
      <br />
      {errors?.map((a, index) => (
        <ErrorCard
          key={index}
          compact={isCompact}
          error={a}
          onStateChanged={() => {
            if (searchId) {
              searchById();
            } else {
              getData();
            }
          }}
        />
      ))}
    </div>
  );
};
export default ErrorLogs;

const ErrorCard = ({
  error,
  compact,
  onStateChanged,
}: {
  error: IErrorModel;
  compact?: boolean;
  onStateChanged: () => void;
}) => {
  const [isCurlModalVisible, setIsCurlModalVisible] = useState(false);
  const [curlCommand, setCurlCommand] = useState("");

  const archiveError = useCallback(() => {
    Api.put(`/api/admin/error-logger/archive/${error._id}`).then(() => {
      onStateChanged();
    });
  }, [error._id, onStateChanged]);

  const deleteError = useCallback(() => {
    Api.delete(`/api/admin/error-logger/${error._id}`).then(() => {
      onStateChanged();
    });
  }, [error._id, onStateChanged]);

  const generateCurlCommand = useCallback(() => {
    const baseUrl = window.location.origin;
    const method = error.parameters?.method || "GET";
    const url = error.parameters?.url || "";
    const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;

    let curl = `curl -X ${method.toUpperCase()} "${fullUrl}"`;

    // Add headers
    const headers: string[] = [];
    headers.push('"Content-Type: application/json"');

    // Add authorization header if userID exists
    if (error.parameters?.userID) {
      headers.push(`"Authorization: Bearer <token>"`);
    }

    if (headers.length > 0) {
      curl += ` \\\n  -H ${headers.join(" \\\n  -H ")}`;
    }

    // Add query parameters
    if (
      error.parameters?.query &&
      Object.keys(error.parameters.query).length > 0
    ) {
      const queryParams = new URLSearchParams();
      Object.entries(error.parameters.query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        const separator = fullUrl.includes("?") ? "&" : "?";
        curl = curl.replace(
          `"${fullUrl}"`,
          `"${fullUrl}${separator}${queryString}"`
        );
      }
    }

    // Add body for POST, PUT, PATCH requests
    if (
      ["POST", "PUT", "PATCH"].includes(method.toUpperCase()) &&
      error.parameters?.body
    ) {
      const bodyJson = JSON.stringify(error.parameters.body);
      // Escape double quotes and backslashes for shell
      const escapedBody = bodyJson.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      curl += ` \\\n  -d "${escapedBody}"`;
    }

    return curl;
  }, [error.parameters]);

  const handleMakeCurl = useCallback(() => {
    const curl = generateCurlCommand();
    setCurlCommand(curl);
    setIsCurlModalVisible(true);
  }, [generateCurlCommand]);

  const handleCopyCurl = useCallback(() => {
    navigator.clipboard
      .writeText(curlCommand)
      .then(() => {
        message.success("Curl command copied to clipboard!");
      })
      .catch(() => {
        message.error("Failed to copy curl command");
      });
  }, [curlCommand]);

  const menuItems: MenuProps["items"] = [
    {
      key: "archive",
      label: "Archive",
      icon: <SaveOutlined />,
      onClick: archiveError,
      disabled: error.isArchive,
    },
    {
      key: "curl",
      label: "Make Curl",
      icon: <CopyOutlined />,
      onClick: handleMakeCurl,
    },
  ];

  const renderObject = (obj: any, indent = 0) => {
    if (!obj || typeof obj !== "object") {
      return <span>{String(obj)}</span>;
    }

    return (
      <div style={{ marginLeft: indent > 0 ? (compact ? 8 : 20) : 0, wordBreak: "break-word" }}>
        {Object.entries(obj).map(([key, value]) => (
          <div key={key} style={{ marginBottom: 8 }}>
            <b>{key}</b>:{" "}
            {typeof value === "object" && value !== null
              ? renderObject(value, indent + 1)
              : <span style={{ overflowWrap: "anywhere" }}>{String(value)}</span>}
          </div>
        ))}
      </div>
    );
  };

  const actions = (
    <Space>
      {error.isArchive && (
        <span
          style={{
            background: "#f0f0f0",
            padding: "2px 8px",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          Archived
        </span>
      )}
      <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
        <Button type="link" size="small" icon={<MoreOutlined />} />
      </Dropdown>
      <Popconfirm
        onConfirm={deleteError}
        okText="Yes"
        cancelText="No"
        title="Are you sure to delete this error?"
      >
        <Button type="link" size="small">
          <DeleteOutlined style={{ color: "red" }} />
        </Button>
      </Popconfirm>
    </Space>
  );

  return (
    <Card
      style={{ marginBottom: 10, position: "relative" }}
      bodyStyle={{ overflowWrap: "anywhere" }}
      title={
        <div style={{ backgroundColor: "#f0f0f0", whiteSpace: "normal" }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            {new Date(error.date ?? "").toLocaleString()}
          </div>
          <div style={{ fontSize: 14, wordBreak: "break-word" }}>{error.filename}</div>
          <div style={{ fontSize: 14, wordBreak: "break-all" }}>{error.parameters?.url}</div>
        </div>
      }
      extra={compact ? undefined : actions}
    >
      {compact && <div style={{ marginBottom: 8 }}>{actions}</div>}
      <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
        ID: {error._id}
      </div>

      {error.parameters && (
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontWeight: "bold",
              marginBottom: 8,
              fontSize: 13,
              color: "#1890ff",
            }}
          >
            Parameters:
          </div>
          {renderObject(error.parameters)}
        </div>
      )}

      {error.details && (
        <div>
          <div
            style={{
              fontWeight: "bold",
              marginBottom: 8,
              fontSize: 13,
              color: "#ff4d4f",
            }}
          >
            Error Details:
          </div>
          {renderObject(error.details)}
        </div>
      )}

      <Modal
        title="Curl Command"
        open={isCurlModalVisible}
        onCancel={() => setIsCurlModalVisible(false)}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={handleCopyCurl}
          >
            Copy
          </Button>,
          <Button key="close" onClick={() => setIsCurlModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={compact ? "100%" : 800}
      >
        <Input.TextArea
          value={curlCommand}
          onChange={(e) => setCurlCommand(e.target.value)}
          rows={15}
          style={{ fontFamily: "monospace", fontSize: 12 }}
        />
      </Modal>
    </Card>
  );
};
